'use client'

import { getChatsByClient } from '@/lib/api/atendimento/chat'
import {
    Attachment,
    Client,
    getClient,
    Note,
    printClient,
} from '@/lib/api/clients/client-api'
import { Chat, ChatStatus } from '@/lib/api/common/interfaces/message.interface'
import { formatPhoneNumber } from '@/lib/form-state/util'
import { formatDateOnly } from '@/lib/utils/date-only'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdAttachFile,
    MdAccountBalanceWallet,
    MdBadge,
    MdBook,
    MdBusiness,
    MdCalendarToday,
    MdChat,
    MdCheckCircle,
    MdDownload,
    MdEdit,
    MdEmail,
    MdHandshake,
    MdInfo,
    MdLocationOn,
    MdMedicalServices,
    MdNote,
    MdPerson,
    MdPhone,
    MdPrint,
    MdSchedule,
    MdStars,
    MdSupportAgent,
    MdSwapHoriz,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import ActionsMenu, { ActionMenuItem } from '@/components/actions-menu'
import { PdfDelivery } from '@/lib/download-blob'
import { WalletAssignModal } from '@/components/wallet/wallet-assign-modal'
import { toast } from 'react-toastify'

const GENDER_LABELS: Record<string, string> = {
    MALE: 'Masculino',
    FEMALE: 'Feminino',
    OTHER: 'Outro',
    PREFER_NOT_TO_SAY: 'Prefiro não informar',
}

interface ClientDetailPageProps {
    /** Renderiza embutido (modal) para este id, ignorando a rota. */
    idOverride?: number
    /** Substitui o "voltar para a lista" por fechar o modal. */
    onBack?: () => void
}

export default function ClientDetailPage({
    idOverride,
    onBack,
}: ClientDetailPageProps = {}) {
    const { user } = useAuth()
    const { clientLabel, ClientLabel } = useClientLabel()
    const navigate = useNavigate()

    // Carteira do cliente: atribuir ou trocar sem sair do detalhe.
    const [showWalletModal, setShowWalletModal] = useState(false)
    const { canUpdate } = useResourcePermissions('person', 'clients')
    const { canRead: canReadMedicalRecord } = useResourcePermissions(
        'medical',
        'medical-records'
    )
    const { id: paramId } = useParams<{ id: string }>()
    const id = idOverride ? String(idOverride) : paramId
    /** No modal, sair da lista significa fechar; na rota, navegar. */
    const goBack = () => (onBack ? onBack() : navigate('/person/clients'))
    const [client, setClient] = useState<Client | null>(null)
    const [chats, setChats] = useState<Chat[]>([])
    const [loading, setLoading] = useState(true)
    const [loadingChats, setLoadingChats] = useState(true)
    const [printing, setPrinting] = useState(false)

    /**
     * O prontuário é o ÚNICO caminho para receituário e atestados desde que o módulo
     * `medical` saiu do menu (ver `sidebar-config.ts`), então quem decide quem entra
     * é a permissão, não só o segmento: `medical:medical-records:read`.
     *
     * O segmento continua no teste porque prontuário não faz sentido fora da saúde —
     * um salão não tem o que registrar ali.
     */
    const canShowMedicalRecord =
        user?.company?.segment === 'HEALTH' && canReadMedicalRecord

    useEffect(() => {
        if (id) {
            loadClient()
            loadChats()
        }
    }, [id])

    const loadClient = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getClient(parseInt(id, 10))
            if (response.success && response.result) {
                setClient(response.result)
            } else {
                toast.error(response.error || `Erro ao carregar ${clientLabel}`)
                goBack()
            }
        } catch (error) {
            toast.error(`Erro ao carregar ${clientLabel}`)
            goBack()
        } finally {
            setLoading(false)
        }
    }

    const loadChats = async () => {
        if (!id || !user?.companyId) return

        setLoadingChats(true)
        try {
            const response = await getChatsByClient(
                parseInt(id, 10),
                user.companyId
            )
            if (response.success) {
                setChats(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar atendimentos')
            }
        } catch (error) {
            toast.error('Erro ao carregar atendimentos')
        } finally {
            setLoadingChats(false)
        }
    }

    /**
     * Ficha cadastral em PDF. `delivery` separa as duas intenções: "Imprimir" abre
     * o diálogo de impressão direto (a recepção quer o papel), "Baixar PDF" salva
     * o arquivo.
     */
    const handlePrint = async (delivery: PdfDelivery) => {
        if (!client) return
        setPrinting(true)
        try {
            await printClient(client.id, delivery)
            if (delivery === 'download') toast.success('PDF gerado com sucesso!')
        } catch {
            toast.error('Erro ao gerar o PDF da ficha')
        } finally {
            setPrinting(false)
        }
    }

    const getStatusConfig = (status: ChatStatus) => {
        switch (status) {
            case ChatStatus.OPEN:
                return {
                    label: 'Em Aberto',
                    className: 'badge-warning',
                    icon: MdSchedule,
                }
            case ChatStatus.CLOSED:
                return {
                    label: 'Fechado',
                    className: 'badge-success',
                    icon: MdCheckCircle,
                }
            default:
                return {
                    label: status,
                    className: 'badge-ghost',
                    icon: MdChat,
                }
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando detalhes do {clientLabel}...
                </p>
            </div>
        )
    }

    if (!client) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdPerson className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        {ClientLabel} não encontrado
                    </h3>
                    <p className="text-sm text-base-content/60">
                        O {clientLabel} que você está procurando não existe
                    </p>
                </div>
                <button onClick={() => goBack()} className="btn btn-secondary">
                    Voltar para {clientLabel}s
                </button>
            </div>
        )
    }

    /**
     * Ações secundárias do cabeçalho. Prontuário e Editar continuam fora do menu:
     * são as duas que o operador procura de olho na tela.
     */
    const clientActions: ActionMenuItem[] = [
        {
            key: 'print',
            label: 'Imprimir ficha',
            icon: <MdPrint />,
            onClick: () => handlePrint('print'),
            loading: printing,
        },
        {
            key: 'download',
            label: 'Baixar PDF',
            icon: <MdDownload />,
            onClick: () => handlePrint('download'),
            loading: printing,
        },
    ]

    const fullAddress = [
        client.street,
        client.number,
        client.complement,
        client.neighborhood,
        client.city,
        client.state,
        client.zipCode,
    ]
        .filter(Boolean)
        .join(', ')

    return (
        <div className="flex flex-col gap-6 p-4 max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => goBack()}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 hidden md:flex lg:flex rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                            <MdPerson className="text-2xl text-white" />
                        </div>
                        <div>
                            <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                {client.name}
                            </h1>
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm text-base-content/60">
                                    Detalhes do {clientLabel}
                                </p>
                                <span className="badge badge-sm gap-1 bg-amber-100 text-amber-700 border-amber-200">
                                    <MdStars className="text-sm" />
                                    {(client.pointsBalance ?? 0).toLocaleString(
                                        'pt-BR'
                                    )}{' '}
                                    {(client.pointsBalance ?? 0) === 1
                                        ? 'ponto'
                                        : 'pontos'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {canShowMedicalRecord && (
                        <button
                            onClick={() =>
                                navigate(
                                    `/person/clients/${client.id}/prontuario`
                                )
                            }
                            className="btn btn-secondary gap-2"
                        >
                            <MdBook className="text-lg" />
                            Prontuário
                        </button>
                    )}
                    {canUpdate && (
                        <button
                            onClick={() =>
                                navigate(`/person/clients/edit/${client.id}`)
                            }
                            className="btn btn-secondary gap-2"
                            title={`Editar ${clientLabel}`}
                        >
                            <MdEdit className="text-lg" />
                        </button>
                    )}
                    {/* Imprimir e baixar ficam num menu, e não em mais dois botões:
                        o cabeçalho já tem Prontuário e Editar, e é o mesmo lugar em
                        que essas ações estão nas telas de consulta e exame. */}
                    <ActionsMenu items={clientActions} />
                </div>
            </div>

            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-base-content/70">
                            <MdCalendarToday />
                            <span>
                                Criado em{' '}
                                {format(
                                    new Date(client.createdAt),
                                    "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                    { locale: ptBR }
                                )}
                            </span>
                        </div>
                        {client.updatedAt !== client.createdAt && (
                            <div className="flex items-center gap-2 text-sm text-base-content/70">
                                <span>
                                    Atualizado em{' '}
                                    {format(
                                        new Date(client.updatedAt),
                                        "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                                        { locale: ptBR }
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-6">
                    {/* Carteira dona do relacionamento comercial. Global por
                        organização — vale em qualquer unidade. */}
                    <div className="flex items-center gap-3 p-4 bg-base-200/50 rounded-xl mb-6">
                        <div className="p-2 bg-secondary/10 rounded-lg">
                            <MdAccountBalanceWallet className="text-xl text-secondary" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-base-content/60 mb-1">
                                Carteira
                            </p>
                            <p className="text-sm font-semibold text-base-content">
                                {client.wallet?.name || 'Sem carteira'}
                            </p>
                        </div>
                        {!client.wallet && (
                            <span className="badge badge-sm badge-warning badge-outline">
                                No pool
                            </span>
                        )}
                        {canUpdate && (
                            <button
                                className="btn btn-sm btn-ghost gap-1"
                                onClick={() => setShowWalletModal(true)}
                            >
                                <MdSwapHoriz className="text-base" />
                                {client.wallet ? 'Trocar' : 'Atribuir'}
                            </button>
                        )}
                    </div>

                    <h3 className="text-lg font-bold text-base-content mb-4">
                        Informações de Contato
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                        {client.cpf && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdBadge className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        CPF
                                    </p>
                                    <p className="text-sm font-semibold text-base-content">
                                        {client.cpf.replace(
                                            /(\d{3})(\d{3})(\d{3})(\d{2})/,
                                            '$1.$2.$3-$4'
                                        )}
                                    </p>
                                </div>
                            </div>
                        )}

                        {client.email && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-info/10 rounded-lg">
                                    <MdEmail className="text-xl text-info" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Email
                                    </p>
                                    <a
                                        href={`mailto:${client.email}`}
                                        className="text-sm font-semibold text-info hover:underline break-all"
                                    >
                                        {client.email}
                                    </a>
                                </div>
                            </div>
                        )}

                        {client.whatsAppNumber && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-success/10 rounded-lg">
                                    <MdPhone className="text-xl text-success" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        WhatsApp
                                    </p>
                                    <a
                                        href={`https://wa.me/${client.whatsAppNumber.replace(
                                            /\D/g,
                                            ''
                                        )}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-semibold text-success hover:underline"
                                    >
                                        {formatPhoneNumber(
                                            client.whatsAppNumber
                                        )}
                                    </a>
                                </div>
                            </div>
                        )}

                        {client.birthDate && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-secondary/10 rounded-lg">
                                    <MdCalendarToday className="text-xl text-secondary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Data de Nascimento
                                    </p>
                                    <p className="text-sm font-semibold text-base-content">
                                        {formatDateOnly(client.birthDate)}
                                    </p>
                                </div>
                            </div>
                        )}

                        {client.gender && (
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl">
                                <div className="p-2 bg-accent/10 rounded-lg">
                                    <MdPerson className="text-xl text-accent" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-base-content/60 mb-1">
                                        Gênero
                                    </p>
                                    <p className="text-sm font-semibold text-base-content">
                                        {GENDER_LABELS[client.gender] ||
                                            client.gender}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {fullAddress && (
                        <>
                            <h3 className="text-lg font-bold text-base-content mb-4">
                                Endereço
                            </h3>
                            <div className="flex items-start gap-3 p-4 bg-base-200/50 rounded-xl mb-6">
                                <div className="p-2 bg-warning/10 rounded-lg">
                                    <MdLocationOn className="text-xl text-warning" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm text-base-content">
                                        {fullAddress}
                                    </p>
                                </div>
                            </div>
                        </>
                    )}

                    {!client.email &&
                        !client.whatsAppNumber &&
                        !client.birthDate &&
                        !client.gender &&
                        !fullAddress && (
                            <div className="text-center py-8 text-base-content/60">
                                <p>Nenhuma informação adicional disponível</p>
                            </div>
                        )}
                </div>
            </div>

            {/* Vinculações */}
            {(client.partner || client.professional) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {client.partner && (
                        <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-warning/20 shadow-sm overflow-hidden">
                            <div className="px-5 py-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-warning/10 rounded-lg">
                                        <MdHandshake className="text-xl text-warning" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-base-content/60 mb-0.5">
                                            Parceiro Vinculado
                                        </p>
                                        <p className="text-sm font-semibold text-base-content truncate">
                                            {client.partner.name ||
                                                client.partner.tradingName}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {client.professional && (
                        <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-secondary/20 shadow-sm overflow-hidden">
                            <div className="px-5 py-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-secondary/10 rounded-lg">
                                        <MdMedicalServices className="text-xl text-secondary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-base-content/60 mb-0.5">
                                            Profissional Responsável
                                        </p>
                                        <p className="text-sm font-semibold text-base-content truncate">
                                            {client.professional.name}
                                        </p>
                                        {client.professional.formation && (
                                            <p className="text-xs text-base-content/50 truncate">
                                                {client.professional.formation}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {client.metadata &&
                Array.isArray(client.metadata) &&
                client.metadata.length > 0 && (
                    <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                        <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                            <div className="flex items-center gap-2">
                                <MdInfo className="text-xl text-secondary" />
                                <h3 className="text-lg font-bold text-base-content">
                                    Informações Adicionais
                                </h3>
                                <span className="badge badge-secondary badge-sm">
                                    {client.metadata.length}
                                </span>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {(
                                    client.metadata as {
                                        key: string
                                        value: string
                                    }[]
                                ).map((item, index) => (
                                    <div
                                        key={index}
                                        className="flex flex-col gap-1 p-4 bg-base-200/50 rounded-xl"
                                    >
                                        <p className="text-xs text-base-content/60 font-medium">
                                            {item.key}
                                        </p>
                                        <p className="text-sm text-base-content whitespace-pre-wrap">
                                            {item.value}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                    <div className="flex items-center gap-2">
                        <MdChat className="text-xl text-secondary" />
                        <h3 className="text-lg font-bold text-base-content">
                            Atendimentos
                        </h3>
                        <span className="badge badge-secondary badge-sm">
                            {chats.length}
                        </span>
                    </div>
                </div>

                <div className="p-6">
                    {loadingChats ? (
                        <div className="flex flex-col items-center justify-center py-8 gap-4">
                            <div className="loading loading-spinner loading-lg text-secondary"></div>
                            <p className="text-base-content/60 text-sm">
                                Carregando atendimentos...
                            </p>
                        </div>
                    ) : chats.length === 0 ? (
                        <div className="text-center py-8 text-base-content/60">
                            <MdChat className="text-4xl mx-auto mb-3 text-base-content/20" />
                            <p>Nenhum atendimento encontrado</p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {chats.map(chat => {
                                const statusConfig = getStatusConfig(
                                    chat.status
                                )
                                const StatusIcon = statusConfig.icon

                                return (
                                    <div
                                        key={chat.id}
                                        onClick={() =>
                                            navigate(
                                                `/atendimento/chats/${chat.id}`,
                                                {
                                                    state: {
                                                        clientId: client.id,
                                                    },
                                                }
                                            )
                                        }
                                        className="group bg-base-200/50 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg hover:border-base-300 transition-all duration-300 cursor-pointer"
                                    >
                                        <div className="p-4">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <h4 className="text-base font-bold text-base-content">
                                                            Atendimento #
                                                            {chat.id}
                                                        </h4>
                                                        <span
                                                            className={`badge ${statusConfig.className} badge-sm text-white gap-1`}
                                                        >
                                                            <StatusIcon className="text-xs" />
                                                            {statusConfig.label}
                                                        </span>
                                                    </div>

                                                    <div className="flex flex-col gap-1.5 mb-3">
                                                        <div className="flex items-center gap-2 text-sm">
                                                            <MdPhone className="text-secondary" />
                                                            <span className="text-base-content/80">
                                                                {formatPhoneNumber(
                                                                    chat.phoneNumber
                                                                )}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm">
                                                            <MdCalendarToday className="text-base-content/50" />
                                                            <span className="text-base-content/70">
                                                                {format(
                                                                    new Date(
                                                                        chat.createdAt
                                                                    ),
                                                                    "dd/MM/yyyy 'às' HH:mm",
                                                                    {
                                                                        locale: ptBR,
                                                                    }
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {(chat.department ||
                                                        chat.attendant) && (
                                                        <div className="flex flex-wrap gap-2 mb-3">
                                                            {chat.department && (
                                                                <span className="badge badge-sm bg-secondary/10 text-secondary border-secondary/20 gap-1">
                                                                    <MdBusiness className="text-xs" />
                                                                    {
                                                                        chat
                                                                            .department
                                                                            .name
                                                                    }
                                                                </span>
                                                            )}
                                                            {chat.attendant && (
                                                                <span className="badge badge-sm bg-secondary/10 text-secondary border-secondary/20 gap-1">
                                                                    <MdSupportAgent className="text-xs" />
                                                                    {
                                                                        chat
                                                                            .attendant
                                                                            .name
                                                                    }
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}

                                                    {chat.summary && (
                                                        <div className="bg-base-100 rounded-lg p-3">
                                                            <p className="text-sm text-base-content/70 line-clamp-2">
                                                                {chat.summary}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>

            {client.attachments &&
                Array.isArray(client.attachments) &&
                client.attachments.length > 0 && (
                    <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                        <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                            <div className="flex items-center gap-2">
                                <MdAttachFile className="text-xl text-secondary" />
                                <h3 className="text-lg font-bold text-base-content">
                                    Anexos
                                </h3>
                                <span className="badge badge-secondary badge-sm">
                                    {client.attachments.length}
                                </span>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="flex flex-col gap-3">
                                {(client.attachments as Attachment[]).map(
                                    (attachment, index) => (
                                        <div
                                            key={index}
                                            className="bg-base-200/50 rounded-xl border border-base-300/50 p-4"
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <MdAttachFile className="text-lg text-secondary" />
                                                        <a
                                                            href={
                                                                attachment.filePath
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="font-semibold text-secondary hover:underline"
                                                        >
                                                            {
                                                                attachment.fileName
                                                            }
                                                        </a>
                                                    </div>
                                                    <div className="space-y-1 text-sm">
                                                        <p>
                                                            <span className="font-medium">
                                                                Usuário:
                                                            </span>{' '}
                                                            {
                                                                attachment.userName
                                                            }
                                                        </p>
                                                        <p>
                                                            <span className="font-medium">
                                                                Descrição:
                                                            </span>{' '}
                                                            {
                                                                attachment.description
                                                            }
                                                        </p>
                                                        {attachment.details && (
                                                            <p>
                                                                <span className="font-medium">
                                                                    Detalhes:
                                                                </span>{' '}
                                                                {
                                                                    attachment.details
                                                                }
                                                            </p>
                                                        )}
                                                        {attachment.createdAt && (
                                                            <p className="text-base-content/60">
                                                                <span className="font-medium">
                                                                    Data:
                                                                </span>{' '}
                                                                {format(
                                                                    new Date(
                                                                        attachment.createdAt
                                                                    ),
                                                                    "dd/MM/yyyy 'às' HH:mm",
                                                                    {
                                                                        locale: ptBR,
                                                                    }
                                                                )}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                )}

            {client.notes &&
                Array.isArray(client.notes) &&
                client.notes.length > 0 && (
                    <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                        <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-6 py-4 border-b border-base-300/50">
                            <div className="flex items-center gap-2">
                                <MdNote className="text-xl text-secondary" />
                                <h3 className="text-lg font-bold text-base-content">
                                    Anotações
                                </h3>
                                <span className="badge badge-secondary badge-sm">
                                    {client.notes.length}
                                </span>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="flex flex-col gap-3">
                                {(client.notes as Note[]).map((note, index) => (
                                    <div
                                        key={index}
                                        className="bg-base-200/50 rounded-xl border border-base-300/50 p-4"
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <MdNote className="text-lg text-secondary" />
                                                    <span className="font-semibold">
                                                        {note.userName}
                                                    </span>
                                                </div>
                                                <p className="text-sm whitespace-pre-wrap mb-2">
                                                    {note.note}
                                                </p>
                                                {note.createdAt && (
                                                    <p className="text-xs text-base-content/60">
                                                        {format(
                                                            new Date(
                                                                note.createdAt
                                                            ),
                                                            "dd/MM/yyyy 'às' HH:mm",
                                                            {
                                                                locale: ptBR,
                                                            }
                                                        )}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

            {/* Carteira do cliente */}
            <WalletAssignModal
                isOpen={showWalletModal}
                clientId={client.id}
                currentWalletId={client.wallet?.id ?? null}
                clientName={client.name}
                clientLabel={clientLabel}
                // Na ficha, tirar a carteira e devolver ao pool é operação legítima.
                allowRemove
                onClose={() => setShowWalletModal(false)}
                onSaved={loadClient}
            />
        </div>
    )
}
